<script lang="ts">
  import { api } from '$shared/api';
  import { addToast } from '$shared/toasts.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import { normalizePicks } from '$lib/utils/normalizePicks';

  interface Style {
    key: string;
    label: string;
  }

  interface GenreGroup {
    genre_id: number;
    genre_name: string;
    styles: Style[];
  }

  const MAX_PICKS = 5;

  const genreColors: Record<string, string> = {
    Electronic: 'var(--cyan)',
    Pop: 'var(--pink)',
    Rock: 'var(--purple)',
    'Hip Hop': '#ff9800',
    Jazz: '#9c27b0',
    Country: '#795548',
    'Funk & Soul': '#e91e63',
    Classical: '#607d8b',
    Latin: '#ff5722',
    Reggae: '#4caf50',
    Blues: '#2196f3',
    'Stage & Screen': '#ffc107',
    'Non-Music': '#9e9e9e',
    'Brass & Military': '#827717',
  };

  let genres = $state<GenreGroup[]>([]);
  let selected = $state<Set<string>>(new Set());
  let loading = $state(true);
  let saving = $state(false);
  let hasChanges = $state(false);
  let originalPicks = $state<string[]>([]);
  let spotifyLoading = $state(false);
  let spotifySuggested = $state(false);
  // Captured from the /users/current fetch in loadData. Drives whether the
  // bottom CTA shows 'Import from Spotify' (connected) or 'Connect Spotify
  // in Account' (not connected) — avoids a confusing 'Could not connect'
  // toast as the only signal that they need to set up Spotify first.
  let spotifyConnected = $state(false);

  let selectedCount = $derived(selected.size);
  // Set of style keys currently visible on the page — used to filter Spotify
  // import results so we never pre-select a style the user can't see/deselect
  // (e.g. one that was hidden via style_allowlist.active=FALSE).
  let visibleStyleKeys = $derived(new Set(genres.flatMap((g) => g.styles.map((s) => s.key))));

  $effect(() => {
    loadData();
  });

  async function loadData() {
    try {
      // Fetch available styles. ?artists=0 skips the 21M-row `track` catalog
      // aggregation that backs onboarding's per-chip artist hints — AccountGenres
      // doesn't render those hints, so the cold-cache cost was pure waste here.
      genres = await api.get<GenreGroup[]>('/users/styles?artists=0');

      // Fetch user's current preferences + Spotify connection state.
      const user = await api.get<{
        user: { preferred_styles: unknown; spotify_id?: string | null };
      }>('/users/current');
      const validKeys = new Set(genres.flatMap((g) => g.styles.map((s) => s.key)));
      const prefs = normalizePicks(user.user?.preferred_styles, validKeys);
      originalPicks = [...prefs];
      selected = new Set(prefs);
      spotifyConnected = user.user?.spotify_id != null;
    } catch (e) {
      console.error('Failed to load genre preferences:', e);
    } finally {
      loading = false;
    }
  }

  function toggle(key: string) {
    if (saving) return;
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else if (next.size < MAX_PICKS) {
      next.add(key);
    }
    selected = next;
    hasChanges = !setsEqual(next, new Set(originalPicks));
  }

  function setsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const item of a) if (!b.has(item)) return false;
    return true;
  }

  async function save() {
    if (selected.size === 0 || saving) return;
    saving = true;
    try {
      await api.put('/users/current/styles', {
        styles: [...selected],
      });
      originalPicks = [...selected];
      hasChanges = false;
      addToast('Genre preferences saved', 'join');
    } catch (e) {
      addToast((e as Error).message || 'Failed to save', 'error');
    } finally {
      saving = false;
    }
  }

  function colorFor(name: string): string {
    return genreColors[name] || 'var(--cyan)';
  }

  async function loadSpotifyStyles() {
    if (spotifyLoading) return;
    spotifyLoading = true;
    try {
      const res = await api.get<{ styles: string[] }>('/users/current/spotify-genres');
      // Filter to styles still visible on the page — Spotify's match set can
      // include keys we've since hidden via style_allowlist.active.
      const candidates = res.styles.filter((s) => visibleStyleKeys.has(s));
      if (candidates.length > 0) {
        const next = new Set(selected);
        let added = 0;
        for (const s of candidates) {
          if (next.size >= MAX_PICKS) break;
          if (!next.has(s)) {
            next.add(s);
            added++;
          }
        }
        selected = next;
        spotifySuggested = true;
        hasChanges = !setsEqual(next, new Set(originalPicks));
        addToast(
          added > 0
            ? `Added ${added} ${added === 1 ? 'style' : 'styles'} from your Spotify!`
            : `Your Spotify matches were already selected.`,
          'follow',
        );
      } else {
        addToast('No matching styles found from Spotify.', 'join');
      }
    } catch {
      addToast('Could not connect to Spotify', 'error');
    } finally {
      spotifyLoading = false;
    }
  }
</script>

<div>
  <h1>Your Genres</h1>
  <p class="subtitle">
    Pick up to {MAX_PICKS} styles you love. This personalizes your duels, games, and Daily Discovery.
  </p>

  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else}
    <div class="pick-count" class:warn={selectedCount >= MAX_PICKS}>
      {selectedCount} / {MAX_PICKS} selected
    </div>

    <div class="connect-section">
      {#if spotifyLoading}
        <span class="text-muted">Loading your Spotify taste...</span>
      {:else if spotifySuggested}
        <span class="text-muted">Styles suggested from your Spotify</span>
      {:else if spotifyConnected}
        <button class="btn-link" onclick={loadSpotifyStyles} type="button">Import from Spotify</button>
      {:else}
        <span class="text-muted">
          Connected to Spotify? <a class="connect-link" href="#/account">Set it up in Account</a> to import your taste.
        </span>
      {/if}
    </div>

    <div class="genre-sections">
      {#each genres as group}
        {@const color = colorFor(group.genre_name)}
        <div class="genre-section">
          <h2 class="genre-heading" style="color: {color}">{group.genre_name}</h2>
          <div class="chips">
            {#each group.styles as sub}
              {@const isSelected = selected.has(sub.key)}
              {@const atMax = selectedCount >= MAX_PICKS && !isSelected}
              <button
                class="chip"
                class:selected={isSelected}
                class:disabled={atMax}
                style="--accent: {color}"
                onclick={() => toggle(sub.key)}
                disabled={atMax}
                aria-pressed={isSelected}
              >
                {#if isSelected}
                  <svg class="check-icon" viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                {/if}
                {sub.label}
              </button>
            {/each}
          </div>
        </div>
      {/each}
    </div>

    {#if hasChanges}
      <button class="btn-save" onclick={save} disabled={saving || selected.size === 0}>
        {saving ? 'Saving...' : 'Save Preferences'}
      </button>
    {/if}

    {#if originalPicks.length === 0}
      <p class="hint">
        You haven't picked any genres yet. Your games and recommendations will be more relevant once you do!
      </p>
    {/if}
  {/if}
</div>

<style>
  h1 {
    margin-bottom: 0.5rem;
  }
  .subtitle {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .genre-sections {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  .genre-heading {
    font-size: 0.9rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin: 0 0 0.5rem;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.85rem;
    border-radius: 100px;
    border: 1.5px solid var(--border);
    background: transparent;
    color: var(--text-secondary);
    font-size: 0.85rem;
    font-weight: 500;
    transition: all 0.2s;
    user-select: none;
  }
  .chip:hover:not(.disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }
  .chip.selected {
    background: var(--accent);
    border-color: var(--accent);
    color: #000;
    font-weight: 600;
  }
  .chip.disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  .check-icon {
    flex-shrink: 0;
  }

  .pick-count {
    margin-top: 1rem;
    font-size: 0.85rem;
    color: var(--text-muted);
  }
  .pick-count.warn {
    color: var(--gold, #ffd700);
  }

  .btn-save {
    display: inline-block;
    margin-top: 1rem;
    padding: 0.6rem 2rem;
    background: linear-gradient(135deg, var(--pink), var(--purple));
    color: #fff;
    font-weight: 700;
    font-size: 0.9rem;
    border: none;
    border-radius: var(--radius);
    cursor: pointer;
  }
  .btn-save:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .hint {
    margin-top: 1rem;
    font-size: 0.85rem;
    color: var(--text-secondary);
    padding: 0.75rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }

  .connect-section {
    margin-top: 1.5rem;
    font-size: 0.85rem;
    color: var(--text-muted);
    text-align: center;
  }
  .btn-link {
    background: none;
    border: none;
    color: var(--text-secondary);
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.85rem;
    padding: 0;
    transition: color 0.2s;
  }
  .btn-link:hover {
    color: var(--text);
  }
  .text-muted {
    color: var(--text-muted);
  }
  .connect-link {
    color: var(--cyan);
    text-decoration: underline;
  }
  .connect-link:hover {
    color: var(--text);
  }
</style>
